import React from 'react';
import { ScreenTypes, ScreenType, IFilePageContent, IFAQPageContent, ISimpleTextPageContent, IFormattedTextPageContent, IParentScreenContent, IWebViewScreenContent, IWebLinkScreenContent } from '../MobileAppTypes';
import { IScreenEditorProps, ScreenEditorFactory } from './SharedTypes';
import FilePageEditor from './FilePageEditor';
import FAQPageEditor from './FAQPageEditor';
import SimpleTextPageEditor from './SimpleTextPageEditor';
import FormattedTextPageEditor from './FormattedTextPageEditor';
import ParentScreenEditor from './ParentScreenEditor';
import WebViewScreenEditor from './WebViewScreenEditor';
import WebLinkScreenEditor from './WebLinkScreenEditor';

/**
 * Registry of all available screen editors
 */
const SCREEN_EDITOR_REGISTRY: Record<ScreenType, ScreenEditorFactory> = {
  [ScreenTypes.FILE_PAGE]: (props: IScreenEditorProps) => (
    <FilePageEditor {...props} content={props.content as IFilePageContent} />
  ),
  [ScreenTypes.FAQ_PAGE]: (props: IScreenEditorProps) => (
    <FAQPageEditor {...props} content={props.content as IFAQPageContent} />
  ),
  [ScreenTypes.SIMPLE_TEXT_PAGE]: (props: IScreenEditorProps) => (
    <SimpleTextPageEditor {...props} content={props.content as ISimpleTextPageContent} />
  ),
  [ScreenTypes.FORMATTED_TEXT_PAGE]: (props: IScreenEditorProps) => (
    <FormattedTextPageEditor {...props} content={props.content as IFormattedTextPageContent} />
  ),
  [ScreenTypes.PARENT_SCREEN]: (props: IScreenEditorProps) => (
    <ParentScreenEditor {...props} content={props.content as IParentScreenContent} />
  ),
  [ScreenTypes.NOTIFICATION_SCREEN]: (props: IScreenEditorProps) => {
    const NotificationScreenEditor = require('./NotificationScreenEditor').default;
    return <NotificationScreenEditor {...props} content={props.content} />;
  },
  [ScreenTypes.WEBVIEW_SCREEN]: (props: IScreenEditorProps) => (
    <WebViewScreenEditor {...props} content={props.content as IWebViewScreenContent} />
  ),
  [ScreenTypes.WEBLINK_SCREEN]: (props: IScreenEditorProps) => (
    <WebLinkScreenEditor {...props} content={props.content as IWebLinkScreenContent} />
  )
};

/**
 * Factory function to create the appropriate screen editor component
 * @param screenType The type of screen editor to create
 * @param props The props to pass to the editor
 * @returns JSX element for the specific screen editor
 */
export const createScreenEditor = (
  screenType: ScreenType,
  props: IScreenEditorProps
): JSX.Element => {
  const editorFactory = SCREEN_EDITOR_REGISTRY[screenType];
  
  if (!editorFactory) {
    console.warn(`No editor found for screen type: ${screenType}. Falling back to SimpleTextPageEditor.`);
    return SCREEN_EDITOR_REGISTRY[ScreenTypes.SIMPLE_TEXT_PAGE](props);
  }
  
  return editorFactory(props);
};

/**
 * Get all registered screen types
 */
export const getRegisteredScreenTypes = (): ScreenType[] => {
  return Object.keys(SCREEN_EDITOR_REGISTRY) as ScreenType[];
};

/**
 * Check if a screen type has a registered editor
 */
export const isScreenTypeSupported = (screenType: string): boolean => {
  return screenType in SCREEN_EDITOR_REGISTRY;
};

/**
 * Add a new screen editor to the registry (for future extensibility)
 */
export const registerScreenEditor = (
  screenType: ScreenType,
  editorFactory: ScreenEditorFactory
): void => {
  SCREEN_EDITOR_REGISTRY[screenType] = editorFactory;
};